import * as THREE from 'three';
import { SketchRelationKind } from '../../../../sketch/solver/constraint/relation-compatibility';
import { SingleTargetRelation } from '../../../../sketch/solver/constraint/relationDefinition/SingleTargetRelation';
import type { SketchEllipseObject } from '../../../../mesh/sketch/SketchEllipseObject';
import { resolvePreferredEdgeLabel } from '../../model/EdgeLabelUtils';
import type { ReferenceDrivenEllipseArcOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseArcOnCurveConstraint';

export type { ReferenceDrivenEllipseArcOnCurveConstraintOptions } from '../../../../../types/reference/sketch-entity-reference/ReferenceDrivenEllipseArcOnCurveConstraint';

/**
 * 参考驱动椭圆弧在曲线上关系定义。
 *
 * 关系实例只保存基础椭圆，部分椭圆的起止分支由参数化方程和当前目标参数共同保持。
 */
export class ReferenceDrivenEllipseArcOnCurveConstraint extends SingleTargetRelation<SketchEllipseObject> {
	/** 引用记录稳定标识。 */
	public readonly referenceId: string;
	/** 关系面板使用的关系类型。 */
	public relationType = SketchRelationKind.OnEdge;

	/** 投影椭圆的世界坐标中心。 */
	private readonly projectedCenterWorld: THREE.Vector3;
	/** 投影椭圆局部 X 轴半径。 */
	private readonly projectedRadiusX: number;
	/** 投影椭圆局部 Y 轴半径。 */
	private readonly projectedRadiusY: number;
	/** 投影椭圆在草图平面内的旋转角。 */
	private readonly projectedRotation: number;
	/** 关系面板显示的来源边标签。 */
	private readonly edgeLabel: string;

	/** @param options 外部椭圆弧投影参数。 */
	public constructor(options: ReferenceDrivenEllipseArcOnCurveConstraintOptions) {
		super();
		this.referenceId = options.referenceId;
		this.projectedCenterWorld = options.projectedCenterWorld.clone();
		this.projectedRadiusX = options.projectedRadiusX;
		this.projectedRadiusY = options.projectedRadiusY;
		this.projectedRotation = options.projectedRotation;
		this.edgeLabel = resolvePreferredEdgeLabel({
			runtimeSourceName: options.runtimeSourceName,
			persistedSourceDisplayLabel: options.sourceDisplayLabel,
		});
		this.name = this.edgeLabel;
	}

	/** @returns 关系面板使用的稳定显示名。 */
	public override get baseDisplayName(): string {
		return this.edgeLabel;
	}

	/** @returns 与当前投影载荷相互独立的新关系实例。 */
	public override cloneSelf(): ReferenceDrivenEllipseArcOnCurveConstraint {
		return new ReferenceDrivenEllipseArcOnCurveConstraint({
			referenceId: this.referenceId,
			projectedCenterWorld: this.projectedCenterWorld.clone(),
			projectedRadiusX: this.projectedRadiusX,
			projectedRadiusY: this.projectedRadiusY,
			projectedRotation: this.projectedRotation,
		});
	}

	/**
	 * 创建统一参数方程使用的局部基础椭圆载荷。
	 *
	 * @returns 椭圆中心及正向长短轴端点；目标尚未绑定时返回 `null`。
	 */
	public override createParametricFormulaPayload() {
		const target = this.getTargetOrThrow();
		if (!target) {
			return null;
		}

		const center = target.referencePlane.worldToLocal(this.projectedCenterWorld);
		const cosine = Math.cos(this.projectedRotation);
		const sine = Math.sin(this.projectedRotation);
		return {
			kind: 'external-ellipse',
			referenceId: this.referenceId,
			center: [center.x, center.y],
			majorPositive: [
				center.x + cosine * this.projectedRadiusX,
				center.y + sine * this.projectedRadiusX,
			],
			minorPositive: [
				center.x - sine * this.projectedRadiusY,
				center.y + cosine * this.projectedRadiusY,
			],
		};
	}
}
